import React from "react";
import classNames from "classnames/bind";
import styles from "../../AddJobModal.module.scss";
import { JobFormData, JobFormErrors } from "../../types/add-job.types";
import {
  LeadSourceOptions,
  ShingleOptions,
  CurrentStageOptions,
  pitchValuePattern,
  squaresValuePattern,
} from "../../constants/add-job.constants";
import { isCustomerReferralLeadSource } from "~/utils/helpers/jobStage";
import { ITradeType } from "~/apis/job";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";

const cx = classNames.bind(styles);

interface JobDetailsFormProps {
  formData: JobFormData;
  errors: JobFormErrors;
  tradeTypes: ITradeType[];
  onFieldChange: (field: keyof JobFormData, value: any) => void;
  onErrorFieldChange: (field: keyof JobFormErrors, value: string) => void;
}

const JobDetailsForm: React.FC<JobDetailsFormProps> = ({
  formData,
  errors,
  tradeTypes,
  onFieldChange,
  onErrorFieldChange,
}) => {
  const isCustomShingle = formData.shingle === "CUSTOM";

  const handleLeadSourceChange = (value: string) => {
    const selectedOption = LeadSourceOptions.find((opt) => opt.label === value);
    const nextLeadSource = selectedOption?.value || value;
    onFieldChange("leadSource", nextLeadSource);
    if (!value) {
      onErrorFieldChange("leadSource", "Lead source is required");
    } else {
      onErrorFieldChange("leadSource", "");
    }
    if (!isCustomerReferralLeadSource(nextLeadSource)) {
      onFieldChange("referralName", "");
      onErrorFieldChange("referralName", "");
    }
  };

  const handleTradeTypeChange = (value: string | string[]) => {
    const selectedValues = Array.isArray(value) ? value : [];
    onFieldChange("tradeType", selectedValues);
    if (selectedValues.length === 0) {
      onErrorFieldChange("tradeType", "Trade type is required");
    } else {
      onErrorFieldChange("tradeType", "");
    }
  };

  const handleShingleChange = (value: string) => {
    onFieldChange("shingle", value);
    onErrorFieldChange("shingle", "");
    if (value !== "CUSTOM") {
      onFieldChange("customShingleColor", "");
    }
  };

  return (
    <div className={cx("job-basic-info-container", "pt-[7px] pl-[25px]")}>
      <h2 className={cx("job-details-title")}>Job Details</h2>
      <div className={cx("row")}>
        <div className={cx("item")}>
          <h4 className={cx("item-title")}>Lead Source</h4>
          <PrimarySelectInput
            name="leadSource"
            placeholder="Select source"
            options={LeadSourceOptions}
            value={
              LeadSourceOptions.find((opt) => opt.value === formData.leadSource)
                ?.label || ""
            }
            onChange={(value) => handleLeadSourceChange(value as string)}
            className={cx("select-input")}
            errorMessage={errors.leadSource}
          />
        </div>

        <div className={cx("item")}>
          <h4 className={cx("item-title")}>Name of the Referral (Optional)</h4>
          <PrimaryInput
            name="referralName"
            placeholder="Enter Name"
            value={formData.referralName}
            onChange={(e) => {
              onFieldChange("referralName", e.target.value);
              onErrorFieldChange("referralName", "");
            }}
            className={cx("input")}
            errorMessage={errors.referralName}
          />
        </div>
      </div>

      <div className={cx("row")}>
        <div className={cx("item")}>
          <h4 className={cx("item-title")}>Trade Type</h4>
          <PrimarySelectInput
            name="tradeType"
            placeholder="Select Trade Type"
            multiple
            options={tradeTypes.map((option) => ({
              label: option.name,
              value: option.id.toString(),
            }))}
            value={formData.tradeType}
            onChange={handleTradeTypeChange}
            className={cx("select-input")}
            errorMessage={errors.tradeType}
          />
        </div>

        <div className={cx("item")}>
          <h4 className={cx("item-title")}>Shingle (Optional)</h4>
          <PrimarySelectInput
            name="shingle"
            placeholder="Select color"
            options={ShingleOptions}
            value={formData.shingle}
            onChange={(value) => handleShingleChange(value as string)}
            className={cx("select-input")}
            errorMessage={errors.shingle}
          />
        </div>
      </div>

      {isCustomShingle && (
        <div className={cx("row")}>
          <div className={cx("item")}>
            <h4 className={cx("item-title")}>Custom Color</h4>
            <PrimaryInput
              name="customShingleColor"
              placeholder="Enter custom color"
              value={formData.customShingleColor}
              onChange={(e) =>
                onFieldChange("customShingleColor", e.target.value)
              }
              className={cx("input")}
            />
          </div>
        </div>
      )}

      <div className={cx("row")}>
        <div className={cx("item")}>
          <h4 className={cx("item-title")}>Shingles Manufacturer (Optional)</h4>
          <PrimaryInput
            name="shinglesManufacturer"
            placeholder="Add Name"
            value={formData.shinglesManufacturer}
            onChange={(e) => {
              onFieldChange("shinglesManufacturer", e.target.value);
              onErrorFieldChange("shinglesManufacturer", "");
            }}
            className={cx("input")}
            errorMessage={errors.shinglesManufacturer}
          />
        </div>

        <div className={cx("item")}>
          <h4 className={cx("item-title")}># of Squares (Optional)</h4>
          <PrimaryInput
            name="numberOfSquares"
            placeholder="Squares"
            value={formData.numberOfSquares}
            onChange={(e) => {
              onFieldChange("numberOfSquares", e.target.value);
              if (e.target.value && !squaresValuePattern.test(e.target.value)) {
                onErrorFieldChange(
                  "numberOfSquares",
                  "# of Squares must be a number or fraction"
                );
              } else {
                onErrorFieldChange("numberOfSquares", "");
              }
            }}
            className={cx("input")}
            errorMessage={errors.numberOfSquares}
          />
        </div>
      </div>

      <div className={cx("row")}>
        <div className={cx("item")}>
          <h4 className={cx("item-title")}>Current Stage (Optional)</h4>
          <PrimarySelectInput
            name="currentStage"
            placeholder="Select Stage"
            options={CurrentStageOptions}
            value={
              CurrentStageOptions.find(
                (opt) => opt.value === formData.currentStage
              )?.label || ""
            }
            onChange={(value) => {
              onFieldChange("currentStage", value as string);
              onErrorFieldChange("currentStage", "");
            }}
            className={cx("select-input")}
            errorMessage={errors.currentStage}
          />
        </div>

        <div className={cx("item")}>
          <h4 className={cx("item-title")}>Pitch (Optional)</h4>
          <PrimaryInput
            name="pitch"
            placeholder="Pitch"
            value={formData.pitch}
            onChange={(e) => {
              onFieldChange("pitch", e.target.value);
              if (e.target.value && !pitchValuePattern.test(e.target.value)) {
                onErrorFieldChange("pitch", "Pitch must be a number, 1/3, or 7-12");
              } else {
                onErrorFieldChange("pitch", "");
              }
            }}
            className={cx("input")}
            errorMessage={errors.pitch}
          />
        </div>
      </div>
    </div>
  );
};

export default JobDetailsForm;
